import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const ABOUT_IMAGE = "https://images.unsplash.com/photo-1768700439764-9ec0ba31ae75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb3J0YXIlMjBhbmQlMjBwZXN0bGUlMjBoZXJicyUyMGF5dXJ2ZWRhfGVufDB8fHx8MTc4MTQxODUzMnww&ixlib=rb-4.1.0&q=85";
const PHIL_IMAGE = "https://images.unsplash.com/photo-1581600140682-d4e68c8cde32?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="bg-oshveda-bgAlt">
        <div className="container-x py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="label-eyebrow mb-4">Our Story</div>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[1.02] text-oshveda-ink">
              Born from <em className="text-oshveda-accent not-italic">grandmother's kitchen.</em>
              <br /> Built for the modern world.
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="text-oshveda-ink2 text-lg leading-relaxed">
              Oshveda Naturals isn't just a brand. It's a promise — that the ancient wisdom of Ayurveda will reach you in its purest, most powerful form.
            </p>
          </div>
        </div>
      </section>

      <section className="section-y bg-oshveda-bg">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden">
              <img src={ABOUT_IMAGE} alt="Mortar pestle" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="label-eyebrow mb-4">The beginning</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-oshveda-ink leading-tight mb-6">
              A simple question that changed everything.
            </h2>
            <p className="text-oshveda-ink2 text-lg leading-relaxed mb-4">
              "Why does the Ayurveda we buy today feel nothing like the one our grandmothers made?" That single question started Oshveda.
            </p>
            <p className="text-oshveda-ink2 text-lg leading-relaxed">
              We travelled to remote villages of Uttarakhand, Kerala and Himachal. We met farmers, vaidyas and herbalists. We learned the slow, patient craft of authentic Ayurvedic preparation — and we promised ourselves we would never compromise on it.
            </p>
          </div>
        </div>
      </section>

      <section className="section-y bg-oshveda-bgAlt">
        <div className="container-x">
          <div className="label-eyebrow mb-3 text-center">What we stand for</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-oshveda-ink text-center mb-14">Three things, non-negotiable.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Source matters.", d: "We don't buy from middlemen. We source directly from farmers and forest collectives — fairly, transparently, sustainably." },
              { n: "02", t: "Time matters.", d: "Real Ayurveda cannot be rushed. Our oils infuse for 41 days. Our churnas are stone-ground. Patience is not optional." },
              { n: "03", t: "Truth matters.", d: "Every batch is tested for purity and heavy metals. Every label says exactly what's inside. No marketing fluff, no shortcuts." },
            ].map((x) => (
              <div key={x.n} data-testid={`value-${x.n}`} className="bg-white rounded-2xl p-8 border border-oshveda-borderSubtle">
                <div className="font-mono text-xs text-oshveda-accent tracking-widest mb-4">{x.n}</div>
                <h3 className="font-serif text-2xl mb-3 text-oshveda-ink">{x.t}</h3>
                <p className="text-oshveda-ink2 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative section-y overflow-hidden">
        <img src={PHIL_IMAGE} alt="herbs" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-oshveda-primary/85" />
        <div className="relative container-x text-center text-oshveda-bg">
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight max-w-3xl mx-auto leading-tight mb-8">
            Discover the <em className="text-oshveda-accent not-italic">authentic Ayurveda</em> your body has been asking for.
          </h2>
          <Link to="/shop" data-testid="about-shop-cta" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-oshveda-bg text-oshveda-primary text-sm tracking-widest uppercase font-semibold hover:bg-oshveda-accent hover:text-white transition-all">
            Explore products <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
